import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Upload, Copy, Trash2, Loader2, Check, ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n";
import api, { API, formatApiErrorDetail } from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const AdminMedia = () => {
  const { user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [copied, setCopied] = useState(null);
  const inputRef = useRef(null);

  const fr = lang === "fr";

  useEffect(() => {
    if (user === false) navigate("/login", { state: { from: "/admin" } });
  }, [user, navigate]);

  const load = async () => {
    try {
      const { data } = await api.get("/media");
      setFiles(data);
    } catch (e) {
      if (e.response?.status === 403) toast.error(fr ? "Accès réservé à l'administrateur." : "Admin only.");
    }
  };

  useEffect(() => {
    if (user && user !== false && user.role === "admin") load();
  }, [user]); // eslint-disable-line

  const onUpload = async (fileList) => {
    const chosen = Array.from(fileList || []);
    if (!chosen.length) return;
    setUploading(true);
    for (const f of chosen) {
      const form = new FormData();
      form.append("file", f);
      try {
        await axios.post(`${API}/media/upload`, form, { withCredentials: true });
      } catch (e) {
        toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload error");
      }
    }
    setUploading(false);
    toast.success(fr ? "Image(s) téléversée(s) !" : "Image(s) uploaded!");
    load();
  };

  const copyUrl = (fileUrl, id) => {
    const full = `${BACKEND}${fileUrl}`;
    navigator.clipboard.writeText(full);
    setCopied(id);
    setTimeout(() => setCopied(null), 1500);
    toast.success(fr ? "Lien copié !" : "Link copied!");
  };

  const remove = async (id) => {
    try {
      await api.delete(`/media/${id}`);
      setFiles((f) => f.filter((x) => x.id !== id));
      toast.success(fr ? "Supprimé." : "Deleted.");
    } catch {
      toast.error("Error");
    }
  };

  if (user && user !== false && user.role !== "admin") {
    return (
      <div className="pt-40 pb-28 text-center min-h-[60vh]">
        <p className="font-serif text-3xl text-foreground">{fr ? "Accès réservé à l'administrateur" : "Admin access only"}</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-media-page" className="pt-36 pb-28 max-w-[1400px] mx-auto px-6 lg:px-10">
      <p className="text-primary/70 text-xs uppercase tracking-[0.25em] mb-6">Admin</p>
      <h1 className="font-serif text-5xl md:text-7xl tracking-tightest text-foreground mb-4">{fr ? "Médiathèque" : "Media Library"}</h1>
      <p className="text-foreground/65 font-light text-lg mb-12 max-w-xl">
        {fr ? "Téléversez vos propres photos, puis copiez leur lien pour les utiliser sur le site." : "Upload your own photos, then copy their link to use them on the site."}
      </p>

      <div
        data-testid="upload-dropzone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); onUpload(e.dataTransfer.files); }}
        className="border-2 border-dashed border-border hover:border-primary transition-colors cursor-pointer rounded-3xl py-16 flex flex-col items-center justify-center text-center mb-12 bg-card"
      >
        {uploading ? <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" /> : <Upload className="w-8 h-8 text-primary mb-4" />}
        <p className="font-serif text-2xl text-foreground mb-2">{fr ? "Glissez vos images ici" : "Drop your images here"}</p>
        <p className="text-sm text-foreground/50">{fr ? "ou cliquez pour parcourir · JPG, PNG, WEBP, GIF, PDF · max 15 Mo" : "or click to browse · JPG, PNG, WEBP, GIF, PDF · max 15MB"}</p>
        <input ref={inputRef} data-testid="file-input" type="file" accept="image/*,.pdf" multiple className="hidden" onChange={(e) => onUpload(e.target.files)} />
      </div>

      {files.length === 0 ? (
        <div className="text-center py-16 text-foreground/40">
          <ImageIcon className="w-10 h-10 mx-auto mb-3" />
          <p>{fr ? "Aucune image pour l'instant." : "No images yet."}</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {files.map((f) => (
            <div key={f.id} data-testid={`media-${f.id}`} className="bg-card border border-border rounded-3xl overflow-hidden group">
              <div className="h-44 bg-muted overflow-hidden flex items-center justify-center">
                {f.content_type?.startsWith("image/") ? (
                  <img src={`${BACKEND}${f.url}`} alt={f.original_filename} className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-10 h-10 text-foreground/30" />
                )}
              </div>
              <div className="p-4">
                <p className="text-sm text-foreground truncate mb-3">{f.original_filename}</p>
                <div className="flex gap-2">
                  <button data-testid={`copy-${f.id}`} onClick={() => copyUrl(f.url, f.id)} className="flex-1 flex items-center justify-center gap-1.5 bg-primary text-primary-foreground text-xs py-2.5 hover:bg-primary/90 transition-colors">
                    {copied === f.id ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {fr ? "Copier le lien" : "Copy link"}
                  </button>
                  <button data-testid={`delete-${f.id}`} onClick={() => remove(f.id)} className="w-10 flex items-center justify-center border border-border text-foreground/60 hover:text-destructive hover:border-destructive transition-colors">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMedia;
